/**
 * Activity Timeline Component
 * Displays recent activity timeline with filtering
 * Following PRD Phase 2 specifications (lines 254-258)
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useDashboardStore } from '../../stores/dashboardStore';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useToast } from '../../hooks/use-toast';
import { cn } from '../../lib/utils';
import {
  Activity,
  AlertCircle,
  Clock,
  Filter,
  ExternalLink,
  RefreshCw,
  Loader2,
  DollarSign,
  BarChart3,
  Database,
  XCircle,
  Wifi
} from 'lucide-react';

interface ActivityTimelineProps {
  maxItems?: number;
  showFilters?: boolean;
  compact?: boolean;
}

type ActivityType = 'transaction' | 'report' | 'sync' | 'error' | 'connection';
type ActivityStatus = 'success' | 'pending' | 'error';

const ActivityTimeline: React.FC<ActivityTimelineProps> = ({
  maxItems = 10,
  showFilters = true,
  compact = false
}) => {
  const { recentActivity, isLoading, error, fetchActivity } = useDashboardStore();
  const { toast } = useToast();
  
  const [filterType, setFilterType] = useState<ActivityType | 'all'>('all');
  const [filterStatus] = useState<ActivityStatus | 'all'>('all');

  const getActivityIcon = (type: ActivityType, status: ActivityStatus) => {
    if (status === 'error') return XCircle;
    if (status === 'pending') return Clock;

    switch (type) {
      case 'transaction':
        return DollarSign;
      case 'report':
        return BarChart3;
      case 'sync':
        return Database;
      case 'connection':
        return Wifi;
      case 'error':
        return AlertCircle;
      default:
        return Activity;
    }
  };

  const getActivityColor = (type: ActivityType, status: ActivityStatus) => {
    if (status === 'error') return 'text-red-600';
    if (status === 'pending') return 'text-yellow-600';

    switch (type) {
      case 'transaction':
        return 'text-green-600';
      case 'report':
        return 'text-blue-600';
      case 'sync':
        return 'text-purple-600';
      case 'connection':
        return 'text-cyan-600';
      case 'error':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const getStatusBadgeVariant = (status: ActivityStatus) => {
    switch (status) {
      case 'success':
        return 'default' as const;
      case 'pending':
        return 'secondary' as const;
      case 'error':
        return 'destructive' as const;
      default:
        return 'outline' as const;
    }
  };

  const formatTimestamp = (timestamp: Date | string | number) => {
    // Robust date handling - handle both Date objects and date strings/numbers
    let dateObj: Date;
    
    try {
      if (timestamp instanceof Date) {
        // Already a Date object
        dateObj = timestamp;
      } else if (typeof timestamp === 'string') {
        // Convert string to Date
        dateObj = new Date(timestamp);
        
        // Check if the date is valid
        if (isNaN(dateObj.getTime())) {
          return 'Invalid date';
        }
      } else if (typeof timestamp === 'number') {
        // Handle timestamp
        dateObj = new Date(timestamp);
        
        if (isNaN(dateObj.getTime())) {
          return 'Invalid timestamp';
        }
      } else {
        // Unknown type
        return 'Invalid date format';
      }
    } catch (error) {
      // Handle any parsing errors
      return 'Date parsing error';
    }

    const now = new Date();
    const diffMs = now.getTime() - dateObj.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    // Format date in IST (Indian Standard Time)
    return dateObj.toLocaleDateString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const handleRefresh = async () => {
    try {
      await fetchActivity();
      toast({
        title: "Activity refreshed",
        description: "Recent activity has been updated",
      });
    } catch (error) {
      toast({
        title: "Refresh failed",
        description: "Unable to refresh activity timeline",
        variant: "destructive",
      });
    }
  };

  const filteredActivities = recentActivity
    .filter(activity => filterType === 'all' || activity.type === filterType)
    .filter(activity => filterStatus === 'all' || activity.status === filterStatus)
    .slice(0, maxItems);

  const typeFilters = [
    { value: 'all', label: 'All Types', icon: Activity },
    { value: 'connection', label: 'Connection', icon: Wifi },
    { value: 'transaction', label: 'Transactions', icon: DollarSign },
    { value: 'report', label: 'Reports', icon: BarChart3 },
    { value: 'sync', label: 'Sync', icon: Database },
    { value: 'error', label: 'Errors', icon: AlertCircle }
  ];

  // Status filters for future implementation
  // const statusFilters = [
  //   { value: 'all', label: 'All Status' },
  //   { value: 'success', label: 'Success' },
  //   { value: 'pending', label: 'Pending' },
  //   { value: 'error', label: 'Error' }
  // ];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className={compact ? "text-base" : "text-lg"}>
              Recent Activity
            </CardTitle>
            <CardDescription>
              Latest actions and system events
            </CardDescription>
          </div>
          
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={isLoading}
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
            </Button>
            
            {!compact && (
              <Button
                variant="outline"
                size="sm"
                asChild
              >
                <Link to="/audit">
                  <ExternalLink className="h-4 w-4 mr-1" />
                  View All
                </Link>
              </Button>
            )}
          </div>
        </div>

        {/* Filters */}
        {showFilters && !compact && (
          <div className="flex flex-wrap gap-2 pt-2">
            <div className="flex items-center space-x-1">
              <Filter className="h-3 w-3 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">Type:</span>
              {typeFilters.map((filter) => (
                <Button
                  key={filter.value}
                  variant={filterType === filter.value ? "default" : "outline"}
                  size="sm"
                  onClick={() => setFilterType(filter.value as ActivityType | 'all')}
                  className="h-6 text-xs"
                >
                  <filter.icon className="h-3 w-3 mr-1" />
                  {filter.label}
                </Button>
              ))}
            </div>
          </div>
        )}
      </CardHeader>

      <CardContent className="p-0">
        {/* Error Display */}
        {error && (
          <div className="p-4 border-b">
            <div className="flex items-center space-x-2 text-red-600">
              <AlertCircle className="h-4 w-4" />
              <p className="text-sm">Failed to load activity: {error}</p>
            </div>
          </div>
        )}

        {/* Loading State */}
        {isLoading && filteredActivities.length === 0 && (
          <div className="p-8 text-center">
            <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">Loading activity...</p>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && filteredActivities.length === 0 && (
          <div className="p-8 text-center">
            <Activity className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">
              {recentActivity.length === 0 
                ? "No activity recorded yet"
                : "No activity matches your filters"
              }
            </p>
          </div>
        )}

        {/* Activity List */}
        {filteredActivities.length > 0 && (
          <div className="divide-y">
            {filteredActivities.map((activity, index) => {
              const ActivityIcon = getActivityIcon(activity.type, activity.status);
              const iconColor = getActivityColor(activity.type, activity.status);
              
              return (
                <div
                  key={activity.id}
                  className={cn(
                    "p-4 hover:bg-muted/50 transition-colors",
                    compact && "p-3"
                  )}
                >
                  <div className="flex items-start space-x-3">
                    {/* Timeline Connector */}
                    <div className="relative">
                      <div className={cn(
                        "flex items-center justify-center rounded-full border-2 border-background",
                        compact ? "h-8 w-8" : "h-10 w-10",
                        activity.status === 'success' && "bg-green-100",
                        activity.status === 'pending' && "bg-yellow-100",
                        activity.status === 'error' && "bg-red-100"
                      )}>
                        <ActivityIcon className={cn(
                          compact ? "h-3 w-3" : "h-4 w-4",
                          iconColor
                        )} />
                      </div>
                      
                      {/* Timeline Line */}
                      {index < filteredActivities.length - 1 && (
                        <div className="absolute top-10 left-1/2 w-0.5 h-8 bg-border transform -translate-x-1/2" />
                      )}
                    </div>

                    {/* Activity Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className={cn(
                          "font-medium",
                          compact ? "text-sm" : "text-base"
                        )}>
                          {activity.description}
                        </p>
                        
                        <div className="flex items-center space-x-2">
                          <Badge variant={getStatusBadgeVariant(activity.status)}>
                            {activity.status}
                          </Badge>
                          <span className={cn(
                            "text-muted-foreground whitespace-nowrap",
                            compact ? "text-xs" : "text-sm"
                          )}>
                            {formatTimestamp(activity.timestamp)}
                          </span>
                        </div>
                      </div>

                      {/* Activity Details */}
                      {activity.details && !compact && (
                        <div className="mt-2 text-sm text-muted-foreground">
                          {typeof activity.details === 'object' ? (
                            <div className="grid grid-cols-2 gap-2">
                              {Object.entries(activity.details).map(([key, value]) => (
                                <div key={key} className="flex space-x-2">
                                  <span className="font-medium capitalize">{key}:</span>
                                  <span>{String(value)}</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p>{activity.details}</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Footer */}
        {!compact && filteredActivities.length > 0 && (
          <div className="p-4 border-t bg-muted/30">
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>
                Showing {filteredActivities.length} of {recentActivity.length} activities
              </span>
              <Button variant="ghost" size="sm" asChild>
                <Link to="/audit">
                  View complete audit log
                  <ExternalLink className="h-3 w-3 ml-1" />
                </Link>
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ActivityTimeline;